import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import hi from './hi.json'
import mr from './mr.json'

/**
 * App-chrome internationalization (D-039/D-040): English / Hindi / Marathi across the
 * storefront and owner console. Default is English; the choice is persisted in localStorage
 * under {@link STORAGE_KEY} and changed via the header switcher ({@link setLanguage}).
 *
 * Only the app's own copy is translated — shop-owned data (product names, branding text,
 * customer-entered notes/addresses) is never translated (§10).
 */
export const SUPPORTED_LANGUAGES = ['en', 'hi', 'mr'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'dukaano.lang'

function isSupported(value: string | null): value is Language {
  return value != null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isSupported(saved)) {
      return saved
    }
  } catch {
    // localStorage unavailable (e.g. private mode) — fall back to English.
  }
  return 'en'
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    hi: { translation: hi },
    mr: { translation: mr },
  },
  lng: initialLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
})

/** Switch language and persist the choice; the header switcher calls this. */
export function setLanguage(lng: Language) {
  void i18n.changeLanguage(lng)
  try {
    localStorage.setItem(STORAGE_KEY, lng)
  } catch {
    // Persistence is best-effort; ignore failures.
  }
}

export default i18n
